import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, stripSearchParams, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { z } from "zod";

import { DateBlock } from "@/components/site/DateBlock";
import { getFcIdTokenWhenReady, useAuth0, useMemberLogin, useMemberRoles } from "@/integrations/fc/auth0";
import { EventAdminPanel } from "@/components/site/EventAdminPanel";
import { meetingQuery, type Meeting } from "@/lib/content";
import { fetchMeetingDetail, type AccessTier, type MeetingDetailResult } from "@/lib/guest-access.functions";
import { meetingDateParts, meetingLocationLabel } from "@/lib/meeting-format";
import { GUEST_TOKEN_PATTERN } from "@/lib/security/guest-access";
import { REGISTRATION_EVENTS, isRegistrationMeeting, meetingHubSpotForm, registrationTitle } from "@/lib/event-registration";
import { EventRegistrationForm } from "@/components/site/EventRegistrationForm";
import { MemberRsvp } from "@/components/site/MemberRsvp";
import { HERO_IMAGE_HEIGHT } from "@/components/site/hero-size";

const searchSchema = z.object({
  guest_token: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/meetings/$slug")({
  staticData: { sitemap: false },
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams({ guest_token: "" })] },
  loader: async ({ context, params }) => {
    const meeting = await context.queryClient.ensureQueryData(meetingQuery(params.slug));
    // Unknown and protected events look identical, so existence is not revealed.
    return meeting ? { title: meeting.title, excerpt: meeting.excerpt } : null;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData && isRegistrationMeeting(params.slug)) {
      return { meta: [
        { title: `${registrationTitle(params.slug)} — Registration — Foreign Council` },
        { name: "description", content: `Event registration for the ${registrationTitle(params.slug)}.` },
        { property: "og:title", content: registrationTitle(params.slug) },
        { property: "og:description", content: `Event registration for the ${registrationTitle(params.slug)}.` },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
        { name: "robots", content: "noindex" },
      ] };
    }
    if (!loaderData) {
      return {
        meta: [
          { title: "Protected meeting — Foreign Council" },
          { name: "description", content: "This Foreign Council meeting requires access." },
          { property: "og:title", content: "Protected meeting — Foreign Council" },
          { property: "og:description", content: "This Foreign Council meeting requires access." },
          { property: "og:type", content: "article" },
          { name: "twitter:card", content: "summary" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const description =
      loaderData.excerpt ??
      `${loaderData.title} — an executive briefing by the Foreign Council On Economic Relations.`;
    const url = `https://foreign-council.org/meetings/${params.slug}`;
    return {
      meta: [
        { title: `${loaderData.title} — Foreign Council` },
        { name: "description", content: description },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: MeetingDetail,
});

function MeetingDetail() {
  const { slug } = Route.useParams();
  const registration = isRegistrationMeeting(slug);
  const { guest_token } = Route.useSearch();
  const navigate = useNavigate({ from: "/meetings/$slug" });
  const { data: publicMeeting } = useSuspenseQuery(meetingQuery(slug));
  const fetchDetail = useServerFn(fetchMeetingDetail);
  const { isAuthenticated, isLoading } = useAuth0();
  const { isAdmin } = useMemberRoles();
  const [view, setView] = useState<"event" | "admin">("event");
  const [detail, setDetail] = useState<Meeting | null>(null);
  const [photos, setPhotos] = useState<MeetingDetailResult["photos"]>([]);
  const [header, setHeader] = useState<MeetingDetailResult["header"]>(null);
  const [guestExpiresAt, setGuestExpiresAt] = useState<string | null>(null);
  const [status, setStatus] = useState<AccessStatus>("idle");
  const [tier, setTier] = useState<AccessTier | null>(null);
  const [verifiedForm, setVerifiedForm] = useState<MeetingDetailResult["registrationForm"]>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const started = useRef(false);
  const memberAttempted = useRef(false);

  const applyResult = (r: MeetingDetailResult) => {
    if (r.meeting) {
      setDetail(r.meeting); setPhotos(r.photos); setHeader(r.header ?? null);
      setGuestExpiresAt(r.tier === "guest" ? r.expiresAt : null); setStatus("idle");
      setTier(r.tier); setVerifiedForm(r.registrationForm ?? null); setEventId(r.eventId ?? null);
    } else setStatus(r.status === "ok" ? "denied" : r.status);
  };

  const load = async (password?: string) => {
    const memberToken = isAuthenticated ? (await getFcIdTokenWhenReady()) ?? undefined : undefined;
    return fetchDetail({ data: { slug, token: tokenRef.current, password, memberToken } });
  };

  useEffect(() => {
    if (isLoading) return;
    if (!started.current) {
      started.current = true;
      if (guest_token) {
        // Remove the token from the address bar and history right away.
        void navigate({ search: { guest_token: "" }, replace: true });
        if (GUEST_TOKEN_PATTERN.test(guest_token)) tokenRef.current = guest_token;
      }
      if (!isAuthenticated) {
        setStatus("checking");
        load().then(applyResult).catch(() => setStatus("denied"));
        return;
      }
    }
    // Members: (re)try once the session is usable. On a fresh page load the first
    // effect run can happen before the token bridge is ready, and a member who
    // signs in while on the page must get a second attempt.
    if (!isAuthenticated || memberAttempted.current || detail) return;
    memberAttempted.current = true;
    setStatus("checking");
    load().then(applyResult).catch(() => setStatus("denied"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest_token, isLoading, isAuthenticated, detail, registration]);

  // Expire an open guest view once its access period ends.
  useEffect(() => {
    if (!guestExpiresAt) return;
    const ms = new Date(guestExpiresAt).getTime() - Date.now();
    if (!Number.isFinite(ms)) return;
    const end = () => { setTier(null); setVerifiedForm(null); setDetail(null); setPhotos([]); setHeader(null); setGuestExpiresAt(null); setStatus("expired"); };
    if (ms <= 0) { end(); return; }
    const t = setTimeout(end, Math.min(ms, 2_147_000_000));
    return () => clearTimeout(t);
  }, [guestExpiresAt]);

  const unlockWithPassword = async (password: string) => {
    setStatus("checking");
    try { applyResult(await load(password)); } catch { setStatus("denied"); }
  };

  const meeting = detail ?? publicMeeting;

  if (!meeting) {
    return (
      <article className="bg-surface">
        <div className="mx-auto w-full max-w-[900px] px-5 py-16 lg:px-8 lg:py-20">
          <Link to="/meetings" className="fc-eyebrow text-accent hover:underline">← All Meetings</Link>
          <h1 className="fc-h2 mt-8 text-foreground">{registrationTitle(slug)}</h1>
          <div className="mt-8 bg-card p-6 lg:p-10">
            <AccessOptions status={status} onPassword={unlockWithPassword} />
          </div>
        </div>
      </article>
    );
  }

  // Registration form is for verified guests only; signed-in members are registered directly.
  const unlocked = tier === "guest" || tier === "member";
  const openForm = tier === "guest" && !isAuthenticated
    ? verifiedForm ?? meetingHubSpotForm(detail) ?? meetingHubSpotForm(publicMeeting) ?? REGISTRATION_EVENTS[slug]?.form ?? null
    : null;
  const hasGuestForm = registration || Boolean(meetingHubSpotForm(publicMeeting));
  const parts = meetingDateParts(meeting);
  const location = meetingLocationLabel(meeting);
  const isUpcoming = meeting.is_upcoming;

  return (
    <article className="bg-surface">
      {header && view === "event" && <figure><img src={header.src} srcSet={`${header.thumbnail} 1400w, ${header.src} 1920w`} sizes="100vw" alt={header.caption || meeting.title} className={`${HERO_IMAGE_HEIGHT} w-full object-cover`} />{header.caption && <figcaption className="mx-auto max-w-[900px] px-5 py-3 text-sm text-muted-foreground">{header.caption}</figcaption>}</figure>}
      <div className="mx-auto w-full max-w-[900px] px-5 py-16 lg:px-8 lg:py-20">
        <Link to="/meetings" className="fc-eyebrow text-accent hover:underline">
          ← All Meetings
        </Link>

        <div className="mt-8">
          {/* Datum oben links, die Rubrik direkt rechts davon auf gleicher Hoehe */}
          <div className="flex items-start gap-4">
            {parts && <DateBlock parts={parts} tone={isUpcoming ? "accent" : "light"} />}
            <span className="fc-eyebrow inline-block max-w-full self-start bg-primary px-3 py-1.5 text-primary-foreground">
              {meeting.category}
            </span>
          </div>
          {location && <p className="fc-location mt-6 text-muted-foreground">{location}</p>}
          <h1 className="fc-h2 mt-2 text-foreground">{meeting.title}</h1>
        </div>

        {isAdmin && <EventTabs view={view} onChange={setView} />}
        {isAdmin && view === "admin" ? (
          <div id="event-tab-panel" role="tabpanel" aria-labelledby="event-tab-admin" className="mt-8 bg-card p-6 lg:p-10">
            <EventAdminPanel slug={slug} eventId={eventId} onImagesChanged={() => { void load().then(applyResult).catch(() => setStatus("denied")); }} />
          </div>
        ) : (<>
        <div className="mt-8 space-y-5 bg-card p-6 lg:p-10" {...(isAdmin ? { id: "event-tab-panel", role: "tabpanel", "aria-labelledby": "event-tab-event" } : {})}>
          {meeting.body ? (
            meeting.body.split("\n\n").map((paragraph, index) => (
              <p key={index} className="fc-body text-foreground">{paragraph}</p>
            ))
          ) : null}
          {/* One access box for everyone: members sign in, guests enter the password. */}
          {!unlocked && (!meeting.body || hasGuestForm) && <AccessOptions status={status} onPassword={unlockWithPassword} />}
          {/* Guests register via the form; members register with one click. */}
          {openForm && <EventRegistrationForm form={openForm} />}
          {tier === "member" && <MemberRsvp eventId={eventId} />}
        </div>

        {photos.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3">
            {photos.map((photo, i) => (
              <figure key={photo.src} className="min-w-0">
              <a href={photo.src} target="_blank" rel="noopener noreferrer" aria-label={photo.caption || `Event photo ${i + 1}`} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                <img src={photo.thumbnail} srcSet={`${photo.thumbnail} 640w, ${photo.src} 1920w`} sizes="(max-width: 767px) 50vw, 300px" alt={photo.caption || `Event photo ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </a>{photo.caption && <figcaption className="mt-2 break-words text-sm text-muted-foreground">{photo.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
        </>)}
      </div>
    </article>
  );
}

function EventTabs({ view, onChange }: { view: "event" | "admin"; onChange: (v: "event" | "admin") => void }) {
  const tabs = [{ id: "event", label: "Event" }, { id: "admin", label: "Admin" }] as const;
  return (
    <div role="tablist" aria-label="Event views" className="mt-8 flex gap-2 border-b border-border"
      onKeyDown={(e) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        const next = view === "event" ? "admin" : "event";
        onChange(next);
        document.getElementById(`event-tab-${next}`)?.focus();
      }}>
      {tabs.map((t) => (
        <button key={t.id} id={`event-tab-${t.id}`} type="button" role="tab" aria-selected={view === t.id}
          aria-controls="event-tab-panel" tabIndex={view === t.id ? 0 : -1} onClick={() => onChange(t.id)}
          className={`-mb-px border-b-2 px-5 py-2 fc-body focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${view === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

type AccessStatus = "idle" | "checking" | "denied" | "expired" | "password_required";

function AccessOptions({
  status,
  onPassword,
}: {
  status: AccessStatus;
  onPassword: (password: string) => void;
}) {
  const login = useMemberLogin();
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (status === "checking") return;
    if (password.length < 6) {
      setLocalError("The password must be at least 6 characters long.");
      return;
    }
    setLocalError("");
    onPassword(password);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <p className="fc-body text-muted-foreground">Members: please sign in to read the full documentation and register.</p>
        <button
          type="button"
          onClick={() => login()}
          className="inline-block bg-primary px-6 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          Login
        </button>
      </div>
      <form onSubmit={submit} className="space-y-3 border-t border-border pt-6">
        <label htmlFor="guest-password" className="fc-body block text-foreground">
          Guests: enter the event password
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="guest-password"
            type="password"
            autoComplete="off"
            minLength={6}
            maxLength={128}
            value={password}
            aria-invalid={localError ? true : undefined}
            aria-describedby="guest-password-message"
            onChange={(e) => {
              setPassword(e.target.value);
              if (localError) setLocalError("");
            }}
            className="min-w-0 flex-1 border border-input bg-background px-4 py-3 fc-body text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          />
          <button
            type="submit"
            disabled={status === "checking"}
            className="bg-foreground px-6 py-3 fc-body text-background transition-opacity hover:opacity-85 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {status === "checking" ? "Checking…" : "Unlock"}
          </button>
        </div>
        <p id="guest-password-message" role="status" aria-live="polite" className="fc-body font-bold text-accent">
          {localError
            ? localError
            : status === "denied"
            ? "This guest access has expired or is invalid."
            : status === "expired"
              ? "This guest access has expired or is invalid. Please contact the organiser for a new link or sign in as a member."
              : status === "password_required"
                ? "Please enter the event password."
                : ""}
        </p>
      </form>
    </div>
  );
}
